import { useState, type ReactNode } from 'react'

import { Button } from 'components/Button'
import { Icon } from 'components/Icon'
import { Input } from 'components/Input'
import { commonStrings } from 'constants/strings'
import { cn } from 'utils/cn'

export type FilterOption<TValue extends string = string> = {
  value: TValue
  label: string
}

type ManagementFilterBarProps = {
  children: ReactNode
}

export function ManagementFilterBar({ children }: ManagementFilterBarProps) {
  // モバイルでは初期状態で折りたたみ、一覧をすぐ見られるようにする（lg以上は常に展開）
  const [open, setOpen] = useState(false)

  return (
    <div className="mt-6">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-2xl border border-border/70 bg-white/70 px-4 py-3 text-sm font-semibold text-ink lg:hidden"
      >
        <span className="flex items-center gap-2">
          <Icon icon="searchInput" className="h-5 w-5 text-muted" />
          {commonStrings.filters.toggleLabel}
        </span>
        <Icon icon={open ? 'chevronUp' : 'chevronDown'} className="h-4 w-4 text-muted" />
      </button>
      <div
        className={cn(
          'flex-wrap items-end gap-3 rounded-2xl border border-border/70 bg-white/70 p-4 lg:mt-0 lg:flex',
          open ? 'mt-2 flex' : 'hidden',
        )}
      >
        {children}
      </div>
    </div>
  )
}

type ManagementSearchInputProps = {
  value: string
  label: string
  placeholder: string
  onChange: (value: string) => void
  onClear: () => void
}

export function ManagementSearchInput({
  value,
  label,
  placeholder,
  onChange,
  onClear,
}: ManagementSearchInputProps) {
  return (
    <label className="grid min-w-[240px] flex-1 gap-2 text-xs font-semibold text-ink">
      {label}
      <span className="relative block">
        <Icon
          icon="searchInput"
          className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted"
        />
        <Input
          type="text"
          role="searchbox"
          value={value}
          placeholder={placeholder}
          className="pl-10 pr-10"
          onChange={(event) => onChange(event.target.value)}
        />
        {value && (
          <button
            type="button"
            className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-muted transition hover:bg-surface hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            aria-label={commonStrings.filters.clearSearch}
            onClick={onClear}
          >
            <Icon icon="close" width={16} height={16} />
          </button>
        )}
      </span>
    </label>
  )
}

type ManagementSelectFilterProps<TValue extends string> = {
  value: TValue | ''
  label: string
  options: FilterOption<TValue>[]
  onChange: (value: TValue | '') => void
  onClear: () => void
  className?: string
}

export function ManagementSelectFilter<TValue extends string>({
  value,
  label,
  options,
  onChange,
  onClear,
  className,
}: ManagementSelectFilterProps<TValue>) {
  return (
    <label
      className={cn(
        'grid min-w-[180px] gap-2 text-xs font-semibold text-ink',
        className,
      )}
    >
      {label}
      <span className="flex items-center gap-2">
        <select
          className="h-11 w-full rounded-2xl border border-border/80 bg-white/80 px-3 text-sm text-ink shadow-sm transition focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
          value={value}
          onChange={(event) => onChange(event.target.value as TValue | '')}
        >
          <option value="">{commonStrings.filters.all}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {value && (
          <button
            type="button"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border/70 bg-white text-muted transition hover:bg-surface hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            aria-label={`${label}${commonStrings.filters.clearFilterSuffix}`}
            onClick={onClear}
          >
            <Icon icon="close" width={16} height={16} />
          </button>
        )}
      </span>
    </label>
  )
}

type ManagementDateRangeFilterProps = {
  label: string
  from: string
  to: string
  onChange: (value: { from: string; to: string }) => void
  onClear: () => void
  className?: string
}

export function ManagementDateRangeFilter({
  label,
  from,
  to,
  onChange,
  onClear,
  className,
}: ManagementDateRangeFilterProps) {
  const hasValue = Boolean(from || to)

  return (
    <div
      className={cn(
        'grid min-w-0 w-full gap-2 text-xs font-semibold text-ink sm:w-auto sm:min-w-[280px]',
        className,
      )}
    >
      <span>{label}</span>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="date"
          value={from}
          max={to || undefined}
          className="min-w-[132px] flex-1"
          aria-label={`${label}${commonStrings.filters.fromSuffix}`}
          onChange={(event) => onChange({ from: event.target.value, to })}
        />
        <span className="text-xs font-semibold text-muted">-</span>
        <Input
          type="date"
          value={to}
          min={from || undefined}
          className="min-w-[132px] flex-1"
          aria-label={`${label}${commonStrings.filters.toSuffix}`}
          onChange={(event) => onChange({ from, to: event.target.value })}
        />
        {hasValue && (
          <button
            type="button"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border/70 bg-white text-muted transition hover:bg-surface hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            aria-label={`${label}${commonStrings.filters.clearFilterSuffix}`}
            onClick={onClear}
          >
            <Icon icon="close" width={16} height={16} />
          </button>
        )}
      </div>
    </div>
  )
}

export const formatResultCount = (filteredCount: number, totalCount: number) =>
  `${commonStrings.filters.resultLabel} ${filteredCount} / ${totalCount}${commonStrings.countSuffix}`

type ManagementPaginationProps = {
  currentPage: number
  totalPage: number
  loading?: boolean
  onPrevious: () => void
  onNext: () => void
}

export function ManagementPagination({
  currentPage,
  totalPage,
  loading = false,
  onPrevious,
  onNext,
}: ManagementPaginationProps) {
  const pageLabel =
    totalPage > 0
      ? `${currentPage} / ${totalPage}`
      : commonStrings.pagination.empty
  const canMovePrev = !loading && totalPage > 0 && currentPage > 1
  const canMoveNext = !loading && totalPage > 0 && currentPage < totalPage

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onPrevious}
        disabled={!canMovePrev}
      >
        {commonStrings.pagination.previous}
      </Button>
      <span className="min-w-[86px] text-center text-xs font-semibold text-muted">
        {pageLabel} {commonStrings.pagination.page}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onNext}
        disabled={!canMoveNext}
      >
        {commonStrings.pagination.next}
      </Button>
    </div>
  )
}

export * from './MultiSelectFilter'
